/**
 * Share Class Card Component
 *
 * Displays and manages share classes (aktieslag).
 */

import { useState } from "react";
import {
  Layers,
  Plus,
  Edit,
  Trash2,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useDeleteShareClass } from "@/hooks/use-share-book";
import { AddShareClassModal } from "./AddShareClassModal";
import type { ShareClass } from "@/lib/api";

interface ShareClassCardProps {
  companyId: string;
  shareClasses: ShareClass[];
  canEdit?: boolean;
}

export function ShareClassCard({ companyId, shareClasses, canEdit = true }: ShareClassCardProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClass, setEditingClass] = useState<ShareClass | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ShareClass | null>(null);

  const deleteShareClass = useDeleteShareClass();

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteShareClass.mutateAsync({
        shareClassId: deleteConfirm.id,
        companyId,
      });
      toast.success("Aktieslaget har tagits bort");
      setDeleteConfirm(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Kunde inte ta bort aktieslaget");
    }
  };

  return (
    <>
      <div className="card">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Layers className="text-gray-400" size={24} />
            <h2 className="text-lg font-bold text-gray-900">Aktieslag</h2>
            <span className="text-sm text-gray-500">({shareClasses.length})</span>
          </div>
          {canEdit && (
            <Button onClick={() => setShowAddModal(true)}>
              <Plus size={18} className="mr-2" />
              Nytt aktieslag
            </Button>
          )}
        </div>

        {/* Share Classes Grid */}
        {shareClasses.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shareClasses.map((shareClass) => (
              <div
                key={shareClass.id}
                className="p-5 border border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">{shareClass.name}</h3>
                    <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                      Kod: {shareClass.code}
                    </span>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEditingClass(shareClass)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="Redigera"
                      >
                        <Edit size={16} />
                      </button>
                      {shareClass.totalShares === 0 && (
                        <button
                          onClick={() => setDeleteConfirm(shareClass)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Ta bort"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-500">Totalt antal aktier</span>
                    <span className="font-medium text-gray-900">
                      {shareClass.totalShares.toLocaleString("sv-SE")}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-500">Röstvärde</span>
                    <span className="font-medium text-gray-900">
                      {parseFloat(shareClass.votingPower)}
                    </span>
                  </div>
                  {shareClass.parValue && (
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-500">Kvotvärde</span>
                      <span className="font-medium text-gray-900">
                        {parseFloat(shareClass.parValue).toLocaleString("sv-SE")} kr
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between py-1">
                    <span className="text-gray-500">Nummerserie</span>
                    <span className="font-medium text-gray-900">
                      {shareClass.numberSeriesStart}–{shareClass.numberSeriesCurrent || "∞"}
                    </span>
                  </div>
                </div>

                {/* Tags */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {shareClass.isPreference && (
                    <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                      Preferensaktie
                    </span>
                  )}
                  {shareClass.preferenceDividend && (
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                      Utdelning: {parseFloat(shareClass.preferenceDividend).toLocaleString("sv-SE")} kr
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Layers className="mx-auto text-gray-300 mb-3" size={48} />
            <p className="text-gray-600 mb-4">
              Inga aktieslag skapade ännu
            </p>
            {canEdit && (
              <Button onClick={() => setShowAddModal(true)}>
                <Plus size={18} className="mr-2" />
                Skapa aktieslag
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingClass) && (
        <AddShareClassModal
          companyId={companyId}
          isOpen={showAddModal || !!editingClass}
          onClose={() => {
            setShowAddModal(false);
            setEditingClass(null);
          }}
          editingClass={editingClass}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Ta bort aktieslag?
                </h3>
                <p className="text-gray-600">
                  Är du säker på att du vill ta bort aktieslaget{" "}
                  <strong>{deleteConfirm.name}</strong>?
                </p>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteShareClass.isPending}
                className="flex-1"
              >
                Avbryt
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteShareClass.isPending}
                className="flex-1"
              >
                {deleteShareClass.isPending ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Tar bort...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} className="mr-2" />
                    Ta bort
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
